import React from 'react';
import * as Sentry from '@sentry/react';

export type ErrorBoundaryProps = {
	className?: string;
	children?: React.ReactNode;
	errorContent?: ({
		error,
		errorInfo,
	}: {
		error: Error;
		errorInfo: React.ErrorInfo;
		handleError: () => void;
	}) => React.ReactNode;

	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type ErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
	errorInfo: React.ErrorInfo | null;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);

		this.state = this._getInitialState();
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		Sentry.captureException(error);
		this.props.onError?.(error, errorInfo);
		this._setError(error, errorInfo);
	}

	render() {
		const { errorInfo, error, hasError } = this.state;
		console.log(error, errorInfo);

		return hasError && error && errorInfo
			? this._renderError(error, errorInfo)
			: this.props.children;
	}

	private _renderError(error: Error, errorInfo: React.ErrorInfo) {
		const { errorContent = this._renderDefaultError } = this.props;

		return errorContent({ error, errorInfo, handleError: this._handleError });
	}

	private _renderDefaultError = () => <span>Произошла техническая ошибка.</span>;

	private _handleError = () => this._resetError();

	private _setError(error: Error, errorInfo: React.ErrorInfo) {
		this.setState({ hasError: true, error, errorInfo });
	}

	private _resetError() {
		this.setState(this._getInitialState());
	}

	private _getInitialState = () => ({ hasError: false, error: null, errorInfo: null });
}
